/**
 * Platform login API (platform-core auth, not virtual-core).
 * Used when virtual-web is embedded and the user logs in via the Login modal.
 */

function getPlatformBaseUrl(): string {
  const domain =
    process.env.REACT_APP_PLATFORM_API_DOMAIN || process.env.REACT_APP_PLATFORM_API_BASE_URL;
  if (!domain) {
    throw new Error('Missing REACT_APP_PLATFORM_API_DOMAIN (or REACT_APP_PLATFORM_API_BASE_URL)');
  }
  return domain.replace(/\/+$/, '');
}

export type PlatformLoginCredentials = {
  email_address: string;
  password: string;
  remember_me?: boolean | number;
};

export type PlatformLoginResponse = {
  user: Record<string, unknown>;
  api_token?: string;
  session_id: string;
  session?: unknown;
  enabled_modules?: string[];
};

/**
 * Log in against platform-core POST /v1/auth/login.
 * Returns session_id and user; no cookies required (JSON response only).
 */
export async function loginWithPlatform(
  credentials: PlatformLoginCredentials
): Promise<PlatformLoginResponse> {
  const baseUrl = getPlatformBaseUrl();
  const url = `${baseUrl}/v1/auth/login`;

  const body = {
    email_address: credentials.email_address,
    password: credentials.password,
    remember_me: credentials.remember_me === true || credentials.remember_me === 1 ? 1 : 0,
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = (await res.json()) as
    | PlatformLoginResponse
    | { error?: string; message?: string; email_address?: string[]; password?: string[] };

  if (!res.ok) {
    const err = data as { error?: string; message?: string; email_address?: string[]; password?: string[] };
    const msg =
      err?.message ||
      err?.error ||
      (Array.isArray(err?.email_address) ? err.email_address.join(' ') : null) ||
      (Array.isArray(err?.password) ? err.password.join(' ') : null) ||
      `Login failed (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : 'Login failed');
  }

  const out = data as PlatformLoginResponse;
  if (!out.session_id) {
    throw new Error('Login response missing session_id');
  }
  return out;
}
